// Reports › Finance › Agent spend (`/c/{slug}/kpis/reports/finance/agent-spend`; SPEND-KPIS,
// `finance.agentSpend`): what the company's DEs and agents cost — today, this week, this month,
// the averages and the cost per finished task — as six compact tiles, a 30-day bar trend and one
// table per DE/agent with its budget and pace. The figures are the `spend.snapshot` job's sums of
// the cost ledger's agent rows; nothing is shown before it has run (no fabricated zeros). Scoped
// like budget vs actual: managers and finance admins see every agent, a seat holder the agents on
// the seats under theirs. One call on load (the view).
import {
  Badge,
  buttonVariants,
  cn,
  EmptyState,
  InfoTip,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, BotIcon } from "lucide-react";
import { useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { monthOfWeek } from "../de/cost-tab";
import { formatDate, formatMoney, formatRecent } from "../format";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import { FinancePage, MonthSelect, monthLabel, monthsAround } from "./finance-parts";
import { agentSpendQuery } from "./finance-queries";

type View = Awaited<ReturnType<Api["client"]["finance"]["agentSpend"]>>;
type Row = View["rows"][number];
type Pace = NonNullable<Row["budget"]>["status"];

/** The pace badge per budget status; "no budget" (or none) reads as a dash. */
export const PACE: Record<
  Exclude<Pace, "no_budget">,
  { label: string; variant: "success" | "warning" | "danger" }
> = {
  on_track: { label: "On track", variant: "success" },
  ahead_of_pace: { label: "Ahead of pace", variant: "warning" },
  reserve_reached: { label: "Reserve reached", variant: "danger" },
};

function PaceBadge({ budget }: { budget: Row["budget"] }) {
  if (!budget || budget.status === "no_budget")
    return <span className="text-muted-foreground">—</span>;
  const p = PACE[budget.status];
  return (
    <Badge variant={p.variant} data-pace={budget.status}>
      {p.label}
    </Badge>
  );
}

/** 30 days of daily spend as thin bars (inline SVG; each bar names its day and amount). */
function TrendBars({ trend, money }: { trend: View["trend"]; money: (minor: number) => string }) {
  const max = Math.max(0, ...trend.map((t) => t.amount_minor));
  const total = trend.reduce((n, t) => n + t.amount_minor, 0);
  const H = 48;
  const step = 10;
  const first = trend[0];
  const last = trend[trend.length - 1];
  return (
    <figure
      className="m-0 rounded-lg border border-border bg-card px-3 py-2"
      data-slot="spend-trend"
    >
      <figcaption className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
        <span>Last 30 days</span>
        <span className="tabular-nums">{money(total)}</span>
      </figcaption>
      <svg
        role="img"
        aria-label={`Daily agent spend, last ${trend.length} days: ${money(total)} in all, ${money(max)} on the biggest day`}
        viewBox={`0 0 ${Math.max(1, trend.length) * step} ${H}`}
        preserveAspectRatio="none"
        className="mt-1 block h-12 w-full"
      >
        {trend.map((t, i) => {
          const h =
            max > 0 ? Math.max(t.amount_minor > 0 ? 1.5 : 0, (t.amount_minor / max) * H) : 0;
          return (
            <rect
              key={t.day}
              x={i * step + 1}
              y={H - h}
              width={step - 2}
              height={h}
              rx={1}
              fill="var(--chart-1)"
            >
              <title>{`${formatDate(t.day)}: ${money(t.amount_minor)}`}</title>
            </rect>
          );
        })}
        <line
          x1={0}
          x2={trend.length * step}
          y1={H - 0.5}
          y2={H - 0.5}
          stroke="var(--chart-grid)"
        />
      </svg>
      {first && last ? (
        <div className="flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>{formatDate(first.day)}</span>
          <span>{formatDate(last.day)}</span>
        </div>
      ) : null}
    </figure>
  );
}

const TIER_LABEL: Record<View["by_tier"][number]["tier"], string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  vision: "Vision",
  untiered: "Before tiers",
};

/** RUN-EFFORT: the month's run cost per effort tier (from the runs' own cost). */
function TierStrip({ tiers, money }: { tiers: View["by_tier"]; money: (minor: number) => string }) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 text-xs"
      data-slot="spend-tiers"
    >
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        By effort
        <InfoTip label="About spend by effort">
          The month's run cost per effort tier, from each run's own cost: it can differ a little
          from the ledger totals above.
        </InfoTip>
      </span>
      {tiers.map((t) => (
        <span key={t.tier} className="tabular-nums" data-tier={t.tier}>
          <span className="font-medium">{TIER_LABEL[t.tier]}</span> {money(t.amount_minor)}
          <span className="text-muted-foreground"> · {t.runs} runs</span>
        </span>
      ))}
    </div>
  );
}

const KIND = {
  digital_employee: { tag: "DE", title: "Digital employee" },
  agent: { tag: "AG", title: "Agent" },
} as const;

export function AgentSpendScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const focusMonth = monthOfWeek(usePeriod());
  const months = useMemo(() => monthsAround(focusMonth, 12, 0), [focusMonth]);
  const [month, setMonth] = useState(focusMonth);
  const view = useQuery(agentSpendQuery(api, company.id, month));
  const data = view.data;
  const currency = data?.currency ?? "USD";
  const money = (minor: number) => formatMoney(minor, currency);
  const rows = [...(data?.rows ?? [])].sort(
    (a, b) => b.period_minor - a.period_minor || a.name.localeCompare(b.name),
  );
  const totals = data?.totals;
  /** A past month: its last day and week, not today's (the API anchors there). */
  const past = data ? data.period_key < new Date().toISOString().slice(0, 7) : false;

  return (
    <FinancePage
      company={company}
      where="chart"
      title="Agent spend"
      icon={BotIcon}
      screen="finance-agent-spend"
      {...(data ? { count: rows.length } : {})}
      toolbar={
        <>
          <MonthSelect value={month} onChange={setMonth} months={months} />
          <span className="text-xs text-muted-foreground tabular-nums">{currency}</span>
          {data?.computed_at ? (
            <span
              className="text-xs text-muted-foreground tabular-nums"
              title={data.computed_at}
              data-slot="spend-updated"
            >
              Updated {formatRecent(data.computed_at)}
            </span>
          ) : null}
          <InfoTip label="About agent spend">
            Summed from the cost ledger's agent rows (models, tools, compute, sandboxes, storage,
            media) per day, every 15 minutes.
          </InfoTip>
        </>
      }
    >
      {view.isPending ? (
        <Loading label="Loading agent spend" rows={5} />
      ) : view.isError ? (
        <LoadError what="Agent spend" error={view.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={BotIcon}
          title="No agents yet"
          description="DEs and agents on your seats show here once they exist."
        />
      ) : !data?.computed_at || !totals ? (
        <EmptyState
          variant="card"
          icon={BotIcon}
          title="No spend summed yet"
          description="The first sums land within 15 minutes of agent activity."
        />
      ) : (
        <>
          <StatGrid
            dense
            data-slot="spend-totals"
            aria-label={`${monthLabel(month)} agent spend totals`}
          >
            <StatTile
              size="sm"
              label={past ? "Last day" : "Today"}
              value={money(totals.today_minor)}
            />
            <StatTile
              size="sm"
              label={past ? "Last week" : "This week"}
              value={money(totals.week_minor)}
            />
            <StatTile
              size="sm"
              label={past ? "Month" : "This month"}
              value={money(totals.period_minor)}
              detail={monthLabel(month)}
            />
            <StatTile size="sm" label="Avg / day" value={money(totals.avg_day_minor)} />
            <StatTile size="sm" label="Avg / week" value={money(totals.avg_week_minor)} />
            <StatTile
              size="sm"
              label="Per done task"
              value={totals.per_task_minor === null ? "—" : money(totals.per_task_minor)}
              detail={
                <span className="inline-flex items-center gap-1">
                  {totals.tasks_done} done
                  <InfoTip label="About per task">
                    Month's spend over tasks the agent finished this month.
                  </InfoTip>
                </span>
              }
            />
          </StatGrid>
          {data.trend.length ? <TrendBars trend={data.trend} money={money} /> : null}
          {/* An API from before tiers sends no `by_tier`. */}
          {data.by_tier?.length ? <TierStrip tiers={data.by_tier} money={money} /> : null}
          <Table data-slot="agent-spend">
            <TableCaption className="sr-only">
              {monthLabel(month)} agent spend in {currency}, per DE or agent.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">DE or agent</TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  {past ? "Last day" : "Today"}
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Week
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Month
                </TableHead>
                <TableHead scope="col" className="text-right max-lg:hidden">
                  Avg / day
                </TableHead>
                <TableHead scope="col" className="text-right max-2xl:hidden">
                  Avg / week
                </TableHead>
                <TableHead scope="col" className="text-right max-2xl:hidden">
                  Done
                </TableHead>
                <TableHead scope="col" className="text-right max-sm:hidden">
                  <span className="inline-flex items-center gap-1">
                    Per task
                    <InfoTip label="About per task">
                      Month's spend over tasks the agent finished this month.
                    </InfoTip>
                  </span>
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Budget
                </TableHead>
                <TableHead scope="col" className="text-right max-md:hidden">
                  Used
                </TableHead>
                <TableHead scope="col">
                  <span className="inline-flex items-center gap-1">
                    Pace
                    <InfoTip label="About used and pace">
                      Used: agent spend in the budget's period over its limit. Pace: that spend
                      projected to the period's end.
                    </InfoTip>
                  </span>
                </TableHead>
                <TableHead scope="col" className="w-10 max-sm:hidden">
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const kind = KIND[r.kind];
                return (
                  <TableRow
                    key={r.membership_id}
                    data-member={r.membership_id}
                    className="group/row"
                  >
                    <TableHead scope="row" className="py-1.5 font-normal">
                      <span className="flex min-w-0 items-center gap-2">
                        <Badge variant="outline" title={kind.title} className="shrink-0">
                          {kind.tag}
                        </Badge>
                        <span className="flex min-w-0 items-baseline gap-1.5">
                          <span className="truncate font-medium">{r.name}</span>
                          {r.seat_title ? (
                            r.seat_id ? (
                              <Link
                                to="/c/$slug/kpis/chart/seat/$id"
                                params={{ slug: company.slug, id: r.seat_id }}
                                search={keepFocus}
                                className="truncate text-xs text-muted-foreground underline-offset-4 hover:underline max-sm:hidden"
                              >
                                {r.seat_title}
                              </Link>
                            ) : (
                              <span className="truncate text-xs text-muted-foreground max-sm:hidden">
                                {r.seat_title}
                              </span>
                            )
                          ) : null}
                        </span>
                      </span>
                    </TableHead>
                    <TableCell className="py-1.5 text-right tabular-nums max-md:hidden">
                      {money(r.today_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-md:hidden">
                      {money(r.week_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right">
                      <span className="tabular-nums">{money(r.period_minor)}</span>
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-lg:hidden">
                      {money(r.avg_day_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-2xl:hidden">
                      {money(r.avg_week_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-2xl:hidden">
                      {r.tasks_done}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-sm:hidden">
                      {r.per_task_minor === null ? "—" : money(r.per_task_minor)}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-md:hidden">
                      {r.budget ? (
                        <span title={r.budget.inherited ? "From the seat or team" : undefined}>
                          {money(r.budget.limit_minor)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-1.5 text-right tabular-nums max-md:hidden">
                      {r.budget && r.budget.status !== "no_budget" ? (
                        `${Math.round(r.budget.used * 100)}%`
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-1.5">
                      <PaceBadge budget={r.budget} />
                    </TableCell>
                    <TableCell className="py-1.5 max-sm:hidden">
                      {r.seat_id ? (
                        <Link
                          to="/c/$slug/kpis/chart/seat/$id"
                          params={{ slug: company.slug, id: r.seat_id }}
                          search={keepFocus}
                          aria-label={`Open ${r.seat_title ?? r.name}`}
                          title="Open seat"
                          className={cn(
                            buttonVariants({ variant: "ghost", size: "icon-xs" }),
                            "opacity-0 transition-opacity duration-(--dur-fast) group-hover/row:opacity-100 group-focus-within/row:opacity-100 pointer-coarse:opacity-100",
                          )}
                        >
                          <ArrowUpRightIcon aria-hidden="true" />
                        </Link>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </FinancePage>
  );
}
